import React, { useEffect, useState, useCallback } from 'react';
import {
  Save, CheckCircle, XCircle, Eye, EyeOff, RefreshCw, ExternalLink,
  Copy, Check, Info, FolderOpen, ChevronRight, Unplug, Loader2, File as FileIcon
} from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function CloudSettingsPage() {
  const { apiFetch } = useApi();

  // Config state
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // App-Konfiguration form
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [basisOrdner, setBasisOrdner] = useState('');
  const [savingApp, setSavingApp] = useState(false);

  // Auth code flow
  const [authCode, setAuthCode] = useState('');
  const [exchanging, setExchanging] = useState(false);
  const [copied, setCopied] = useState(false);

  // Connection actions
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Folder browser
  const [browserPath, setBrowserPath] = useState('');
  const [browserEntries, setBrowserEntries] = useState(null);
  const [browsing, setBrowsing] = useState(false);

  // Messages
  const [msg, setMsg] = useState('');

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/api/downloads/cloud-config');
      if (r?.ok) {
        const d = await r.json();
        setConfig(d);
        if (d.app_key) setAppKey(d.app_key);
        if (d.basis_ordner) setBasisOrdner(d.basis_ordner);
        if (d.app_secret_masked) setAppSecret(d.app_secret_masked);
      }
    } catch {
      setConfig({ connected: false, error: 'Verbindung fehlgeschlagen' });
    }
    setLoading(false);
  };

  const showMsg = (type, text) => {
    setMsg(`${type}:${text}`);
    if (type === 'success') setTimeout(() => setMsg(''), 5000);
  };
  const isSuccess = msg.startsWith('success:');
  const msgText = msg.replace(/^(success|error):/, '');

  // Save App-Konfiguration
  const saveAppConfig = async () => {
    if (!appKey.trim()) return;
    setSavingApp(true); setMsg('');
    try {
      const r = await apiFetch('/api/downloads/cloud-config', {
        method: 'POST',
        body: JSON.stringify({
          app_key: appKey.trim(),
          app_secret: appSecret.trim(),
          basis_ordner: basisOrdner.trim(),
        })
      });
      const d = await r?.json();
      if (r?.ok) {
        showMsg('success', d?.message || 'Gespeichert');
        loadConfig();
      } else {
        showMsg('error', d?.detail || 'Fehler');
      }
    } catch { showMsg('error', 'Netzwerkfehler'); }
    setSavingApp(false);
  };

  // Auth URL
  const authUrl = appKey
    ? `https://www.dropbox.com/oauth2/authorize?client_id=${encodeURIComponent(appKey)}&response_type=code&token_access_type=offline`
    : '';

  const openAuthUrl = () => { if (authUrl) window.open(authUrl, '_blank'); };
  const copyAuthUrl = () => {
    if (authUrl) {
      navigator.clipboard.writeText(authUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Exchange auth code
  const exchangeCode = async () => {
    if (!appKey.trim() || !authCode.trim()) return;
    // Use stored secret if user hasn't changed it
    const secret = appSecret.startsWith('********') ? '' : appSecret.trim();
    setExchanging(true); setMsg('');
    try {
      const r = await apiFetch('/api/downloads/cloud-config/exchange-code', {
        method: 'POST',
        body: JSON.stringify({ app_key: appKey.trim(), app_secret: secret, auth_code: authCode.trim() })
      });
      const d = await r?.json();
      if (r?.ok) {
        showMsg('success', d?.message || 'Verbindung erfolgreich!');
        setAuthCode('');
        loadConfig();
      } else {
        showMsg('error', d?.detail || 'Fehler');
      }
    } catch { showMsg('error', 'Netzwerkfehler'); }
    setExchanging(false);
  };

  // Test connection
  const testConnection = async () => {
    setTesting(true); setMsg('');
    try {
      const r = await apiFetch('/api/downloads/cloud-config/test', { method: 'POST' });
      const d = await r?.json();
      if (r?.ok) showMsg('success', d?.message || 'Verbindung OK');
      else showMsg('error', d?.detail || 'Test fehlgeschlagen');
    } catch { showMsg('error', 'Netzwerkfehler'); }
    setTesting(false);
  };

  // Disconnect
  const disconnect = async () => {
    if (!window.confirm('Dropbox-Verbindung wirklich trennen?')) return;
    setDisconnecting(true); setMsg('');
    try {
      const r = await apiFetch('/api/downloads/cloud-config', { method: 'DELETE' });
      const d = await r?.json();
      if (r?.ok) {
        showMsg('success', d?.message || 'Getrennt');
        setAuthCode('');
        setBrowserEntries(null);
        loadConfig();
      } else showMsg('error', d?.detail || 'Fehler');
    } catch { showMsg('error', 'Netzwerkfehler'); }
    setDisconnecting(false);
  };

  // Browse folders
  const browseFolders = async (path) => {
    setBrowsing(true);
    const p = path || basisOrdner || '';
    setBrowserPath(p);
    try {
      const r = await apiFetch(`/api/downloads/cloud-config/browse?path=${encodeURIComponent(p)}`);
      if (r?.ok) setBrowserEntries(await r.json());
      else setBrowserEntries([]);
    } catch { setBrowserEntries([]); }
    setBrowsing(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '–';
    try {
      return new Date(iso).toLocaleString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return iso; }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const breadcrumbs = browserPath ? browserPath.split('/').filter(Boolean) : [];

  if (loading) {
    return (
      <div data-testid="cloud-settings-page">
        <div className="page-header"><h2>Cloud-Speicher</h2></div>
        <div className="of-card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={20} className="spin" style={{ marginBottom: 8 }} /> Lade Konfiguration...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cloud-settings-page">
      <div className="page-header">
        <h2>Einstellungen</h2>
        <p>Konfiguration und Systemverwaltung</p>
      </div>

      {/* Section Title + Status Badge */}
      <div className="of-card" style={{ maxWidth: 780, marginBottom: 16, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Dropbox-Verbindung</h3>
          <span data-testid="cloud-status-badge" style={{
            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
            background: config?.connected ? '#D1FAE5' : '#FEE2E2',
            color: config?.connected ? '#065F46' : '#991B1B',
          }}>
            {config?.connected ? 'Verbunden' : 'Nicht verbunden'}
          </span>
        </div>

        {config?.connected && config?.has_refresh_token ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
            Dauerhaft per OAuth-Refresh-Token verbunden – der Zugriff wird automatisch erneuert und läuft nicht mehr ab.
          </p>
        ) : config?.connected ? (
          <p style={{ fontSize: 13, color: '#B45309', margin: 0 }}>
            Verbunden mit einfachem Access-Token – läuft nach ca. 4 Stunden ab.
          </p>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Noch nicht verbunden. Richten Sie die Verbindung in den folgenden Schritten ein.
          </p>
        )}

        {/* Global message */}
        {msg && (
          <div data-testid="cloud-msg" style={{
            padding: '8px 12px', borderRadius: 6, marginTop: 12, fontSize: 13,
            background: isSuccess ? '#D1FAE5' : '#FEE2E2',
            color: isSuccess ? '#065F46' : '#991B1B'
          }}>{msgText}</div>
        )}
      </div>

      {/* Instructions */}
      <div className="of-card" style={{ maxWidth: 780, marginBottom: 16, padding: '16px 24px', background: 'var(--bg-secondary, #f8fafc)', borderLeft: '3px solid var(--primary)' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Info size={16} style={{ color: 'var(--primary)', marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Einrichtung in 3 Schritten:</div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
              <li>
                Öffnen Sie <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>dropbox.com/developers/apps</a>,
                erstellen Sie eine <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>Scoped Access</code>-App mit
                <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>Full Dropbox</code>-Zugriff.
                Unter <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>Permissions</code> aktivieren Sie:
                <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>files.content.write</code>,
                <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>files.content.read</code> → Submit.
              </li>
              <li>
                Kopieren Sie <strong>App key</strong> und <strong>App secret</strong> aus der App-Übersicht und speichern Sie diese hier unten.
              </li>
              <li>
                Klicken Sie auf <strong>Dropbox verbinden</strong>, autorisieren Sie die App im Popup-Fenster, kopieren Sie den angezeigten Code und fügen Sie ihn hier ein.
              </li>
            </ol>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0', fontStyle: 'italic' }}>
              Der gespeicherte Refresh-Token erneuert den Zugriff automatisch – kein Ablauf mehr.
            </p>
          </div>
        </div>
      </div>

      {/* 1. App-Konfiguration */}
      <div className="of-card" style={{ maxWidth: 780, marginBottom: 16, padding: '20px 24px' }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>1. App-Konfiguration</h4>
        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <div>
            <label className="of-label">App key</label>
            <input className="of-input" value={appKey} onChange={e => setAppKey(e.target.value)}
              placeholder="z.B. cgbs6rnfbv2wq" data-testid="cloud-app-key" />
          </div>
          <div>
            <label className="of-label">App secret</label>
            <div style={{ position: 'relative' }}>
              <input className="of-input" type={showSecret ? 'text' : 'password'}
                value={appSecret} onChange={e => setAppSecret(e.target.value)}
                placeholder="z.B. x9y8z7w6v5u4t3s2" data-testid="cloud-app-secret"
                style={{ paddingRight: 36 }} />
              <button type="button" onClick={() => setShowSecret(!showSecret)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {config?.app_secret_masked && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Gespeichert: {config.app_secret_masked}
              </div>
            )}
          </div>
          <div>
            <label className="of-label">Basis-Ordner</label>
            <input className="of-input" value={basisOrdner} onChange={e => setBasisOrdner(e.target.value)}
              placeholder="z.B. /BGPR Dokumente" data-testid="cloud-basis-ordner" />
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={saveAppConfig}
          disabled={savingApp || !appKey.trim()} data-testid="cloud-save-app-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {savingApp ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
          App-Konfiguration speichern
        </button>
      </div>

      {/* 2. Verbindung */}
      <div className="of-card" style={{ maxWidth: 780, marginBottom: 16, padding: '20px 24px' }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>2. Verbindung</h4>

        {config?.connected ? (
          <>
            {/* Connected info */}
            <div style={{
              background: '#ECFDF5', borderRadius: 8, padding: '14px 18px', marginBottom: 16,
              border: '1px solid #A7F3D0'
            }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{config.account_name}</div>
              <div style={{ fontSize: 12, color: '#065F46' }}>{config.account_email}</div>
              {config.connected_since && (
                <div style={{ fontSize: 12, color: '#047857', marginTop: 4 }}>
                  Verbunden seit: {formatDate(config.connected_since)}
                </div>
              )}
              {config.last_token_refresh && (
                <div style={{ fontSize: 12, color: '#047857' }}>
                  Zuletzt erneuert: {formatDate(config.last_token_refresh)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={testConnection} disabled={testing}
                data-testid="cloud-test-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                {testing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                Verbindung testen
              </button>
              <button className="btn btn-sm" onClick={disconnect} disabled={disconnecting}
                data-testid="cloud-disconnect-btn"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
                  background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA',
                }}>
                {disconnecting ? <Loader2 size={14} className="spin" /> : <Unplug size={14} />}
                Verbindung trennen
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Not connected – show auth flow */}
            {!appKey.trim() ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Bitte zuerst App Key und App Secret oben eingeben und speichern.
              </p>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" onClick={openAuthUrl} data-testid="open-auth-url"
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ExternalLink size={14} /> Dropbox verbinden
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={copyAuthUrl}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                    data-testid="copy-auth-url">
                    {copied ? <><Check size={14} /> Kopiert</> : <><Copy size={14} /> Link kopieren</>}
                  </button>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <label className="of-label">Autorisierungscode</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="of-input" value={authCode} onChange={e => setAuthCode(e.target.value)}
                      placeholder="Code von Dropbox einfügen" data-testid="cloud-auth-code"
                      style={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }} />
                    <button className="btn btn-primary btn-sm" onClick={exchangeCode}
                      disabled={exchanging || !authCode.trim()} data-testid="cloud-exchange-btn"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                      {exchanging ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />}
                      Verbindung herstellen
                    </button>
                  </div>
                </div>
              </div>
            )}
            {config?.error && (
              <div style={{
                fontSize: 12, color: '#991B1B', background: '#FEF2F2', padding: '8px 12px',
                borderRadius: 6, marginTop: 12,
              }}>{config.error}</div>
            )}
          </>
        )}
      </div>

      {/* Ordner-Browser */}
      {config?.connected && (
        <div className="of-card" style={{ maxWidth: 780, padding: '20px 24px' }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>Ordner-Browser</h4>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>Durchsuchen Sie Ihre Dropbox-Ordner</p>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => browseFolders(basisOrdner || '')}
              disabled={browsing} data-testid="cloud-browse-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              {browsing ? <Loader2 size={14} className="spin" /> : <FolderOpen size={14} />}
              Stammordner laden
            </button>
          </div>

          {browserEntries !== null && (
            <div>
              {/* Breadcrumbs */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginBottom: 10,
                color: 'var(--text-muted)', flexWrap: 'wrap',
              }}>
                <button onClick={() => browseFolders('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--primary)', fontWeight: 600, fontSize: 12 }}>
                  /
                </button>
                {breadcrumbs.map((crumb, i) => {
                  const path = '/' + breadcrumbs.slice(0, i + 1).join('/');
                  return (
                    <React.Fragment key={i}>
                      <ChevronRight size={12} />
                      <button onClick={() => browseFolders(path)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--primary)', fontWeight: i === breadcrumbs.length - 1 ? 600 : 400, fontSize: 12 }}>
                        {crumb}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Entries */}
              {browserEntries.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 12, textAlign: 'center' }}>
                  Ordner ist leer
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                  {browserEntries.map((entry, i) => (
                    <div key={entry.path} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      fontSize: 12, borderBottom: i < browserEntries.length - 1 ? '1px solid var(--border)' : 'none',
                      cursor: entry.is_folder ? 'pointer' : 'default',
                      background: 'var(--bg-primary)',
                    }}
                      onClick={() => entry.is_folder && browseFolders(entry.path)}
                      onMouseEnter={e => { if (entry.is_folder) e.currentTarget.style.background = 'var(--bg-secondary, #f8fafc)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-primary)'; }}
                    >
                      {entry.is_folder
                        ? <FolderOpen size={14} style={{ color: '#F59E0B', flexShrink: 0 }} />
                        : <FileIcon size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                      <span style={{ flex: 1, fontWeight: entry.is_folder ? 500 : 400 }}>{entry.name}</span>
                      {!entry.is_folder && entry.size != null && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatSize(entry.size)}</span>
                      )}
                      {entry.is_folder && <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
